import { fmt2 } from "@/lib/sqaafLabels";
import { buildGaugeStyle } from "@/utils/sqaafExecutiveDerived";

const BAND_TAG = {
  Critical: "red",
  "Needs Focus": "orange",
  Developing: "amber",
  Strong: "green",
  Exemplary: "blue",
};

const BAND_HEADLINE = {
  Critical: "Selected school requires urgent intervention",
  "Needs Focus": "Selected school needs focused improvement",
  Developing: "Selected school needs focused improvement",
  Strong: "Selected school is performing well",
  Exemplary: "Selected school is an exemplary performer",
};

export function getBandTag(band) {
  return BAND_TAG[band] || "amber";
}

export function truncateSchoolName(name, max = 42) {
  if (!name) return "—";
  const text = String(name).toUpperCase();
  return text.length <= max ? text : `${text.slice(0, max).trim()} ...`;
}

export function getActionPriority(band, gapToDistrict = 0) {
  const gap = Number(gapToDistrict);
  if (band === "Critical" || gap <= -15) return { label: "High", sub: "Assign block mentor" };
  if (band === "Needs Focus" || gap <= -5) return { label: "High", sub: "Assign block mentor" };
  if (band === "Developing") return { label: "Medium", sub: "Schedule section review" };
  return { label: "Track", sub: "Maintain current trajectory" };
}

export function buildDiagnosticSignal(lowestQuestions = []) {
  const blob = lowestQuestions.map((q) => String(q.question || "")).join(" ").toLowerCase();
  const themes = [];
  if (/technology|ict|digital|innovative|pedagog/.test(blob)) themes.push("Technology-enabled learning");
  if (/environment|green|eco/.test(blob)) themes.push("environmental practices");
  if (/attendance/.test(blob)) themes.push("student attendance");
  if (/library|lab|laborator|resource/.test(blob)) themes.push("laboratory/library evidence");
  if (/critical thinking|creativity|problem/.test(blob)) themes.push("higher-order learning skills");
  if (!themes.length) themes.push("Teaching-learning, infrastructure and governance items");
  return `${themes.slice(0, 4).join(", ")} require immediate review before the next SQAAF cycle.`;
}

export function buildSchoolHeroCopy(school = {}, schoolDetail = {}, overview = {}) {
  school = school ?? {};
  schoolDetail = schoolDetail ?? {};
  overview = overview ?? {};
  const score = Number(school["SQAAF_Score_%"] ?? schoolDetail?.school?.["SQAAF_Score_%"] ?? 0);
  const band = school.Band || schoolDetail?.school?.Band || "Developing";
  const name = school.school_name || schoolDetail?.school?.school_name || "Selected school";
  const answered = Number(school["Answered_%"] ?? schoolDetail?.school?.["Answered_%"] ?? overview?.completion_rate ?? 0);
  const districtMean = Number(schoolDetail?.district_mean ?? overview?.district_mean ?? 0);
  const gap = Number(schoolDetail?.gap_to_district_mean ?? score - districtMean);
  const lowCount = schoolDetail?.lowest_10_questions?.length ?? 0;

  let body = `${name} is `;
  if (gap < 0) body += "below the district mean";
  else if (gap > 0) body += "above the district mean";
  else body += "at the district mean";
  if (answered >= 99) body += " but has complete answer coverage. This means the intervention should focus on quality improvement, not data completion.";
  else body += `. Answer coverage is ${fmt2(answered)}%, so completion and quality both need attention.`;

  return {
    gaugeStyle: buildGaugeStyle(score),
    score: fmt2(score),
    headline: BAND_HEADLINE[band] || BAND_HEADLINE.Developing,
    body,
    chips: [
      { label: `${band} Band`, className: "" },
      { label: `${fmt2(answered)}% Answered`, className: "blue" },
      { label: `${lowCount} Low Questions`, className: "red" },
    ],
    signal: buildDiagnosticSignal(schoolDetail?.lowest_10_questions || []),
  };
}

export function buildSchoolHeroMetrics(overview = {}, school = {}, schoolDetail = {}) {
  overview = overview ?? {};
  school = school ?? {};
  schoolDetail = schoolDetail ?? {};
  const score = Number(school["SQAAF_Score_%"] ?? schoolDetail?.school?.["SQAAF_Score_%"] ?? 0);
  const band = school.Band || schoolDetail?.school?.Band || "Developing";
  const districtMean = Number(schoolDetail?.district_mean ?? overview?.district_mean ?? 0);
  const gap = Number(schoolDetail?.gap_to_district_mean ?? score - districtMean);
  const priority = getActionPriority(band, gap);
  const criticalCount =
    overview?.band_counts?.Critical ?? overview?.band_distribution?.find((b) => b.band === "Critical")?.count ?? 0;

  return [
    {
      label: "Total Schools",
      value: Number(overview?.n_schools || 0).toLocaleString(),
      sub: "Across Pune district",
      badge: "",
    },
    {
      label: "Critical Schools",
      value: Number(criticalCount).toLocaleString(),
      sub: "Score below 40%",
      badge: "red",
    },
    {
      label: "Avg Completion",
      value: `${fmt2(overview?.completion_rate)}%`,
      sub: "Answered questions",
      badge: "green",
    },
    {
      label: "Selected School",
      value: fmt2(score),
      sub: `${band} band`,
      badge: "orange",
    },
    {
      label: "Gap to District",
      value: gap > 0 ? `+${fmt2(gap)}` : fmt2(gap),
      sub: `District mean ${fmt2(districtMean)}`,
      badge: gap < 0 ? "red" : "green",
    },
    {
      label: "Action Priority",
      value: priority.label,
      sub: priority.sub,
      badge: "purple",
    },
  ];
}

export function buildProfileChartBars(sectionProfile = []) {
  return (sectionProfile || []).map((row, index) => ({
    section: row.section_label || row.section || `Section ${index + 1}`,
    key: row.section ?? `section-${index}`,
    block: Math.max(4, Math.min(100, Number(row.block_mean) || 0)),
    district: Math.max(4, Math.min(100, Number(row.district_mean) || 0)),
    school: Math.max(4, Math.min(100, Number(row.school_score) || 0)),
    title: `Section ${row.section}: Block ${fmt2(row.block_mean)}, District ${fmt2(row.district_mean)}, School ${fmt2(row.school_score)}`,
  }));
}

export function buildSchoolInsightCards(schoolDetail = {}) {
  const profile = schoolDetail?.section_profile || [];
  const lowSections = profile.filter((r) => {
    const school = Number(r.school_score);
    const block = Number(r.block_mean);
    const district = Number(r.district_mean);
    return school <= 30 && (block >= 50 || district >= 50);
  });
  const strongSections = profile.filter((r) => Number(r.school_score) >= 90);

  const lowExample = lowSections[0];
  const riskBody = lowSections.length
    ? `Several sections have ${fmt2(lowExample?.school_score)}% school score while benchmark values remain near ${fmt2(lowExample?.district_mean || lowExample?.block_mean)}%. This indicates real performance gaps rather than district-wide weakness.`
    : "Review sections where the school trails block and district benchmarks by more than 15 points.";

  const strengthBody = strongSections.length
    ? `${strongSections.length} section${strongSections.length > 1 ? "s" : ""} reach 90–100%. These can be used as evidence anchors while improving low-performing teaching-learning and infrastructure items.`
    : "Identify sections scoring above 85% and use them as evidence anchors during remediation planning.";

  return [
    { tone: "red", title: "Immediate Risk", body: riskBody },
    { tone: "green", title: "Strength Pockets", body: strengthBody },
  ];
}

export function buildSchoolActionQueue(school = {}, schoolDetail = {}) {
  school = school ?? {};
  schoolDetail = schoolDetail ?? {};
  const band = school.Band || schoolDetail?.school?.Band || "Developing";
  const gap = Number(schoolDetail?.gap_to_district_mean ?? 0);
  const score = Number(school["SQAAF_Score_%"] ?? schoolDetail?.school?.["SQAAF_Score_%"] ?? 0);
  const districtMean = Number(schoolDetail?.district_mean ?? 0);
  const answered = Number(school["Answered_%"] ?? schoolDetail?.school?.["Answered_%"] ?? 0);
  const priority = getActionPriority(band, gap);
  const pill = priority.label === "High" ? "High priority" : priority.label === "Medium" ? "Medium priority" : "Track progress";
  const lowestQ = (schoolDetail?.lowest_10_questions || [])[0];
  const weakSection = (schoolDetail?.section_profile || [])
    .slice()
    .sort((a, b) => Number(a.school_score) - Number(b.school_score))[0];
  const targetScore = fmt2(Math.min(100, score + Math.max(5, Math.abs(gap) * 0.5)));

  return {
    pill,
    items: [
      {
        tone: "orange",
        title: lowestQ ? `Section ${lowestQ.section || "—"} Evidence Check` : "Verify Lowest Questions",
        body: lowestQ
          ? `Lowest question scores ${fmt2(lowestQ.score_pct)}% — verify evidence for "${String(lowestQ.question || "").slice(0, 60)}".`
          : "Review lowest-scoring questions and verify supporting evidence.",
        priority: "High",
        priorityClass: "",
      },
      {
        tone: "purple",
        title: weakSection ? `Close Gap in Section ${weakSection.section}` : "Assign Block Mentor",
        body: weakSection
          ? `School ${fmt2(weakSection.school_score)}% vs block ${fmt2(weakSection.block_mean)}% and district ${fmt2(weakSection.district_mean)}%.`
          : `Gap to district is ${gap > 0 ? "+" : ""}${fmt2(gap)} pts — assign block mentor for ${band} band.`,
        priority: "Medium",
        priorityClass: "amber",
      },
      {
        tone: "green",
        title: "Track Next SQAAF Cycle",
        body: answered < 95
          ? `Raise answer coverage from ${fmt2(answered)}% to 100%, then target ${targetScore}% score (district mean ${fmt2(districtMean)}%).`
          : `Target ${targetScore}% score improvement from current ${fmt2(score)}% (district mean ${fmt2(districtMean)}%).`,
        priority: "Track",
        priorityClass: "blue",
      },
    ],
  };
}

export function buildQuestionRows(lowestQuestions = []) {
  return (lowestQuestions || []).map((q) => {
    const pct = Number(q.score_pct);
    let priority = "Low";
    let priorityTag = "red";
    if (pct <= 25) {
      priority = "Low";
      priorityTag = "red";
    } else if (pct <= 50) {
      priority = "Medium";
      priorityTag = "orange";
    } else {
      priority = "Monitor";
      priorityTag = "amber";
    }
    const code = q.section || "";
    return {
      code,
      question: q.question,
      score: fmt2(q.score),
      scorePct: `${fmt2(q.score_pct)}%`,
      priority,
      priorityTag,
    };
  });
}

export function findSelectedSchool(schools = [], selectedCode) {
  if (!selectedCode) return schools[0] || null;
  return schools.find((s) => s.school_code === selectedCode) || schools[0] || null;
}
